import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface Role {
  id: string;
  name: string;
  description: string;
}

interface Connection {
  id: string;
  name: string;
  type: string;
}

interface TableInfo {
  name: string;
  description?: string;
}

interface Permission {
  id?: string;
  roleId: string;
  connectionId: string;
  tableName: string;
  canSelect: boolean;
  canInsert: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

interface PermissionFormData {
  roleId: string;
  connectionId: string;
  tableNames: string[];
  canSelect: boolean;
  canInsert: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

export const TablePermissions: React.FC = () => {
  const { user, token } = useAuth();
  
  // 状态管理
  const [roles, setRoles] = useState<Role[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [tables, setTables] = useState<Record<string, TableInfo[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 对话框状态
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [formData, setFormData] = useState<PermissionFormData>({
    roleId: '',
    connectionId: '',
    tableNames: [],
    canSelect: false,
    canInsert: false,
    canUpdate: false,
    canDelete: false
  });
  
  // 通知状态
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const API_BASE_URL = 'https://d1-mysql-manager-api.5212025.xyz';

  // API调用辅助函数
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: '请求失败' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // 加载基础数据
  const loadRoles = async () => {
    try {
      const data = await apiCall('/api/roles');
      setRoles(data.data?.roles || []);
    } catch (error) {
      console.error('加载角色失败:', error);
      showSnackbar('加载角色失败', 'error');
    }
  };

  const loadConnections = async () => {
    try {
      const data = await apiCall('/api/connections');
      setConnections(data.data?.connections || []);
    } catch (error) {
      console.error('加载连接失败:', error);
      showSnackbar('加载连接失败', 'error');
    }
  };

  const loadTables = async (connectionId: string) => {
    try {
      const data = await apiCall(`/api/connections/${connectionId}/tables`);
      const tableList = data.data?.tables || [];
      setTables(prev => ({
        ...prev,
        [connectionId]: tableList
      }));
    } catch (error) {
      console.error('加载表列表失败:', error);
      showSnackbar('加载表列表失败', 'error');
    }
  };

  const loadPermissions = async () => {
    try {
      setLoading(true);
      // 模拟从角色数据中获取权限信息
      const allPermissions: Permission[] = [];
      
      for (const role of roles) {
        if (role.id) {
          try {
            const roleData = await apiCall(`/api/roles/${encodeURIComponent(role.id)}`);
            const rolePermissions = roleData.data?.permissions || [];
            
            rolePermissions.forEach((perm: any) => {
              allPermissions.push({
                id: perm.id,
                roleId: role.id,
                connectionId: perm.connection_id || 'd1',
                tableName: perm.table_name,
                canSelect: perm.can_select || false,
                canInsert: perm.can_insert || false,
                canUpdate: perm.can_update || false,
                canDelete: perm.can_delete || false
              });
            });
          } catch (error) {
            console.warn(`加载角色 ${role.name} 权限失败:`, error);
            // 不阻断其他角色的加载，继续处理
          }
        }
      }
      
      setPermissions(allPermissions);
    } catch (error) {
      console.error('加载权限失败:', error);
      showSnackbar('加载权限失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 保存权限
  const savePermission = async () => {
    try {
      if (!formData.roleId || !formData.connectionId || !formData.tableNames.length) {
        showSnackbar('请填写完整的权限信息', 'error');
        return;
      }

      // 为每个选中的表创建权限记录
      const permissionsToSet = formData.tableNames.map(tableName => ({
        connectionId: formData.connectionId,
        tableName: tableName,
        canSelect: formData.canSelect,
        canInsert: formData.canInsert,
        canUpdate: formData.canUpdate,
        canDelete: formData.canDelete
      }));

      // 🔧 修复：使用增量权限更新API，避免覆盖现有权限
      console.log('使用增量权限更新API，权限数据:', permissionsToSet);
      
      const response = await apiCall(`/api/roles/${formData.roleId}/permissions`, {
        method: 'PATCH', // 🔧 改为PATCH方法，使用增量更新
        body: JSON.stringify({
          permissions: permissionsToSet
        })
      });

      console.log('增量权限更新成功:', response.data);

      const isEditing = !!editingPermission;
      const message = isEditing 
        ? `成功更新 ${formData.tableNames.length} 个表的权限`
        : `成功为 ${formData.tableNames.length} 个表添加权限`;
        
      showSnackbar(message, 'success');
      setOpenDialog(false);
      resetForm();
      
      // 🔧 修复：重新加载所有相关数据，确保同步
      await loadPermissions();
      await loadRoles(); // 重新加载角色数据，更新权限计数
      
      // 🔧 可选：通知父组件或其他组件刷新角色数据
      // 如果有全局状态管理，可以在这里触发更新事件
    } catch (error) {
      console.error('保存权限失败:', error);
      showSnackbar((error as Error).message || '保存权限失败', 'error');
    }
  };

  // 删除权限
  const deletePermission = async (permission: Permission) => {
    try {
      if (!window.confirm('确定要删除这个权限设置吗？')) return;

      // 🔧 修复：使用新的单个权限删除API，避免重新设置整个角色权限
      console.log('删除单个权限:', permission);
      
      const { roleId, connectionId, tableName } = permission;
      
      // 🔧 修复：对URL参数进行编码，特别是处理表名为*的情况
      const encodedRoleId = encodeURIComponent(roleId);
      const encodedConnectionId = encodeURIComponent(connectionId);
      const encodedTableName = encodeURIComponent(tableName);
      
      console.log('编码后的URL参数:', {
        roleId: encodedRoleId,
        connectionId: encodedConnectionId,
        tableName: encodedTableName
      });
      
      await apiCall(`/api/roles/${encodedRoleId}/permissions/${encodedConnectionId}/${encodedTableName}`, {
        method: 'DELETE'
      });

      showSnackbar('权限删除成功', 'success');
      
      // 重新加载权限和角色数据
      await loadPermissions();
      await loadRoles();
    } catch (error) {
      console.error('删除权限失败:', error);
      showSnackbar((error as Error).message || '删除权限失败', 'error');
    }
  };

  // 表单处理
  const resetForm = () => {
    setFormData({
      roleId: '',
      connectionId: '',
      tableNames: [],
      canSelect: false,
      canInsert: false,
      canUpdate: false,
      canDelete: false
    });
    setEditingPermission(null);
  };

  const handleEdit = (permission: Permission) => {
    setEditingPermission(permission);
    setFormData({
      roleId: permission.roleId,
      connectionId: permission.connectionId,
      tableNames: [permission.tableName],
      canSelect: permission.canSelect,
      canInsert: permission.canInsert,
      canUpdate: permission.canUpdate,
      canDelete: permission.canDelete
    });
    setOpenDialog(true);
  };

  const handleCreate = () => {
    resetForm();
    setOpenDialog(true);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // 获取角色名称
  const getRoleName = (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    return role?.name || roleId;
  };

  // 获取连接名称
  const getConnectionName = (connectionId: string) => {
    const connection = connections.find(c => c.id === connectionId);
    return connection?.name || connectionId;
  };

  // 获取权限标签颜色
  const getPermissionColor = (hasPermission: boolean) => {
    return hasPermission ? 'success' : 'default';
  };

  // 加载连接对应的表
  const handleConnectionChange = (connectionId: string) => {
    setFormData(prev => ({ ...prev, connectionId, tableNames: [] }));
    if (connectionId && !tables[connectionId]) {
      loadTables(connectionId);
    }
  };

  useEffect(() => {
    loadRoles();
    loadConnections();
  }, []);

  useEffect(() => {
    if (roles.length > 0) {
      loadPermissions();
    }
  }, [roles]);

  // 权限检查
  const isAdmin = user?.role?.name === 'Administrator';
  
  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="warning">
          权限不足：只有管理员可以管理表权限设置
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            表权限管理
          </Typography>
          <Typography variant="body1" color="text.secondary">
            管理角色对不同数据库表的访问权限
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          添加权限
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>角色</TableCell>
                  <TableCell>连接</TableCell>
                  <TableCell>表名</TableCell>
                  <TableCell align="center">查询</TableCell>
                  <TableCell align="center">插入</TableCell>
                  <TableCell align="center">更新</TableCell>
                  <TableCell align="center">删除</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.map((permission, index) => (
                  <TableRow key={`${permission.roleId}-${permission.connectionId}-${permission.tableName}-${index}`}>
                    <TableCell>
                      <Chip label={getRoleName(permission.roleId)} color="primary" size="small" />
                    </TableCell>
                    <TableCell>{getConnectionName(permission.connectionId)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={permission.tableName} 
                        variant={permission.tableName === '*' ? 'filled' : 'outlined'}
                        color={permission.tableName === '*' ? 'warning' : 'default'}
                        size="small"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={permission.canSelect ? '✓' : '✗'} 
                        color={getPermissionColor(permission.canSelect)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={permission.canInsert ? '✓' : '✗'} 
                        color={getPermissionColor(permission.canInsert)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={permission.canUpdate ? '✓' : '✗'} 
                        color={getPermissionColor(permission.canUpdate)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={permission.canDelete ? '✓' : '✗'} 
                        color={getPermissionColor(permission.canDelete)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="编辑权限">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEdit(permission)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="删除权限">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => deletePermission(permission)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {permissions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {loading ? '加载中...' : '暂无权限设置'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 权限编辑对话框 */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {editingPermission ? '编辑权限' : '添加权限'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>角色</InputLabel>
              <Select
                value={formData.roleId}
                label="角色"
                onChange={(e) => setFormData(prev => ({ ...prev, roleId: e.target.value }))}
                disabled={!!editingPermission}
              >
                {roles.map(role => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>数据库连接</InputLabel>
              <Select
                value={formData.connectionId}
                label="数据库连接"
                onChange={(e) => handleConnectionChange(e.target.value)}
                disabled={!!editingPermission}
              >
                {connections.map(connection => (
                  <MenuItem key={connection.id} value={connection.id}>
                    {connection.name} ({connection.type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>表名</InputLabel>
              <Select
                multiple
                value={formData.tableNames}
                label="表名"
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  tableNames: typeof e.target.value === 'string' ? 
                    e.target.value.split(',') : e.target.value 
                }))}
                disabled={!!editingPermission || !formData.connectionId}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value="*">* (所有表)</MenuItem>
                {(tables[formData.connectionId] || []).map(table => (
                  <MenuItem key={table.name} value={table.name}>
                    {table.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="h6" gutterBottom>
                权限设置
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.canSelect}
                      onChange={(e) => setFormData(prev => ({ ...prev, canSelect: e.target.checked }))}
                    />
                  }
                  label="查询 (SELECT)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.canInsert}
                      onChange={(e) => setFormData(prev => ({ ...prev, canInsert: e.target.checked }))}
                    />
                  }
                  label="插入 (INSERT)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.canUpdate}
                      onChange={(e) => setFormData(prev => ({ ...prev, canUpdate: e.target.checked }))}
                    />
                  }
                  label="更新 (UPDATE)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.canDelete}
                      onChange={(e) => setFormData(prev => ({ ...prev, canDelete: e.target.checked }))}
                    />
                  }
                  label="删除 (DELETE)"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} startIcon={<CancelIcon />}>
            取消
          </Button>
          <Button onClick={savePermission} variant="contained" startIcon={<SaveIcon />}>
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 